import axios, { AxiosError, AxiosResponse } from "axios";
import envConfig from "../../environment/envConfig";
import { Customer, Event, EventDto, TimeSlot, WasteItem } from "../types/types";
axios.defaults.baseURL = envConfig.apiBaseUrl;

const getEvents = async (): Promise<AxiosResponse | null> => {
  try {
    const council = process.env.REACT_APP_FLAVOUR ?? "";
    const response = await axios.get("chemwaste/getevents", {
      headers: {
        "Content-Type": "application/json",
      },
      params: {
        env: process.env.REACT_APP_ENVIRONMENT,
        code: envConfig.keys.getEventsKey,
        council: council,
      },
    });

    const mapEventDtoToEvent = (eventDto: EventDto): Event => ({
      ...eventDto,
      bookingStartDateTime: new Date(eventDto.bookingStartDateTime),
      bookingEndDateTime: new Date(eventDto.bookingEndDateTime),
      startDateTime: new Date(eventDto.startDateTime),
      endDateTime: new Date(eventDto.endDateTime),
    });

    response.data = response.data.map(mapEventDtoToEvent);
    return response;
  } catch (error) {
    return null;
  }
};

const getTimeSlots = async (eventID: number): Promise<AxiosResponse | null> => {
  try {
    const response = await axios.get("chemwaste/gettimeslots", {
      headers: {
        "Content-Type": "application/json",
      },
      params: {
        event: eventID,
        env: process.env.REACT_APP_ENVIRONMENT,
        code: envConfig.keys.getTimeSlotsKey,
      },
    });

    const mapTimeSlotDtoToTimeSlot = (timeSlotDto: any): TimeSlot => ({
      ...timeSlotDto,
      startTime: new Date(timeSlotDto.startTime),
      endTime: new Date(timeSlotDto.endTime),
    });

    response.data = response.data.map(mapTimeSlotDtoToTimeSlot);
    return response;
  } catch (error) {
    return null;
  }
};

const getCustomerByEmail = async (
  email: string,
  ticketNumber: number
): Promise<AxiosResponse | null> => {
  try {
    const response = await axios.get("chemwaste/customers-by-email", {
      headers: {
        "Content-Type": "application/json",
      },
      params: {
        env: process.env.REACT_APP_ENVIRONMENT === "prod" ? "prod" : "dev",
        code: envConfig.keys.getCustomerByEmailKey,
        email: email,
        ticketNumber: ticketNumber,
      },
    });

    return response;
  } catch (error) {
    const axError = error as AxiosError;

    return axError?.response ?? null;
  }
};

const createCustomer = async (
  customer: Customer
): Promise<AxiosResponse | null> => {
  try {
    const response = await axios.post("chemwaste/createCustomer", customer, {
      headers: {
        "Content-Type": "application/json",
        "x-functions-key": envConfig.keys.createCustomerKey,
      },
      params:
        process.env.REACT_APP_ENVIRONMENT === "prod"
          ? { env: "prod" }
          : { env: "dev" },
    });
    return response;
  } catch (error) {
    return null;
  }
};

const getCustomerLines = async (
  ticketNumber: number
): Promise<AxiosResponse | null> => {
  try {
    const response = await axios.get(
      `chemwaste/getcustomerlines/${ticketNumber}`,
      {
        headers: {
          "Content-Type": "application/json",
        },
        params: {
          env: process.env.REACT_APP_ENVIRONMENT,
          code: envConfig.keys.getCustomerLinesKey,
        },
      }
    );

    return response;
  } catch (error) {
    const axError = error as AxiosError;

    return axError?.response ?? null;
  }
};

const createCustomerLines = async (
  wasteItemList: WasteItem[]
): Promise<AxiosResponse | null> => {
  try {
    const response = await axios.post(
      "chemwaste/createCustomerLines",
      wasteItemList,
      {
        headers: {
          "Content-Type": "application/json",
          "x-functions-key": envConfig.keys.createCustomerLinesKey,
        },
        params:
          process.env.REACT_APP_ENVIRONMENT === "prod"
            ? { env: "prod" }
            : { env: "dev" },
      }
    );
    return response;
  } catch (error) {
    return null;
  }
};

const updateCustomerLines = async (
  wasteItemList: WasteItem[]
): Promise<AxiosResponse | null> => {
  try {
    const response = await axios.put(
      "chemwaste/putcustomerlines",
      wasteItemList,
      {
        headers: {
          "Content-Type": "application/json",
          "x-functions-key": envConfig.keys.putCustomerLinesKey,
        },
        params: {
          env: process.env.REACT_APP_ENVIRONMENT,
          ticketNumber: wasteItemList[0]?.ticketNumber,
        },
      }
    );

    return response;
  } catch (error) {
    return null;
  }
};

export const apiService = {
  getEvents,
  getCustomerByEmail,
  getCustomerLines,
  updateCustomerLines,
  getTimeSlots,
  createCustomer,
  createCustomerLines,
};
